/**
 * BudgetBadgeSubscriber — baseline REST fetch only (no SignalR in v1).
 *
 * For approver-role users, counts:
 *   - pendingBudgetApprovals:  budgets sitting in Submitted (statusId 1)
 *   - unacknowledgedAlerts:    fired budget alerts not yet acknowledged
 *   - pendingChangeRequests:   change requests awaiting a decision (statusId 0)
 *
 * Refetches on window focus and on a 5-minute interval. Mirrors
 * ProcurementBadgeSubscriber's structure minus the SignalR wiring.
 */

import { useCallback, useEffect } from 'react';
import {
  useBudgetService,
  useBudgetAlertService,
  useBudgetChangeRequestService,
} from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { useBudgetBadgesStore } from './budgetBadgesStore';

const APPROVER_ROLES = ['Admin', 'BudgetAdmin'];
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;
const DEBUG = import.meta.env.DEV;
const log = (...args: any[]) => {
  if (DEBUG) console.log('[BudgetBadgeSubscriber]', ...args); // eslint-disable-line no-console
};

export function BudgetBadgeSubscriber() {
  const { hasAnyRole, user } = useEnrichedAuth();
  const isApprover = hasAnyRole(APPROVER_ROLES);
  const isAuthed = !!user;

  const budgetService = useBudgetService();
  const budgetAlertService = useBudgetAlertService();
  const changeRequestService = useBudgetChangeRequestService();
  const setFromMetrics = useBudgetBadgesStore((s) => s.setFromMetrics);
  const reset = useBudgetBadgesStore((s) => s.reset);

  const refetchBaseline = useCallback(async () => {
    if (!isApprover) {
      setFromMetrics({ pendingBudgetApprovals: 0, unacknowledgedAlerts: 0, pendingChangeRequests: 0 });
      return;
    }
    try {
      const [submitted, alerts, pendingChanges] = await Promise.all([
        budgetService.getFiltered({ statusId: 1 }),
        budgetAlertService.getFiltered({ acknowledged: false }),
        changeRequestService.getAll({ statusId: 0 }),
      ]);
      const pendingBudgetApprovals = (submitted ?? []).length;
      const unacknowledgedAlerts = (alerts ?? []).length;
      const pendingChangeRequests = (pendingChanges ?? []).length;
      log('baseline', { pendingBudgetApprovals, unacknowledgedAlerts, pendingChangeRequests });
      setFromMetrics({ pendingBudgetApprovals, unacknowledgedAlerts, pendingChangeRequests });
    } catch (err) {
      log('baseline fetch failed', err);
    }
  }, [isApprover, budgetService, budgetAlertService, changeRequestService, setFromMetrics]);

  // Initial fetch on auth
  useEffect(() => {
    if (!isAuthed) return;
    refetchBaseline();
  }, [isAuthed, refetchBaseline]);

  // Refetch on window focus + 5-minute interval
  useEffect(() => {
    if (!isAuthed || !isApprover) return;
    const onFocus = () => refetchBaseline();
    window.addEventListener('focus', onFocus);
    const interval = window.setInterval(refetchBaseline, REFRESH_INTERVAL_MS);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.clearInterval(interval);
    };
  }, [isAuthed, isApprover, refetchBaseline]);

  useEffect(() => () => reset(), [reset]);

  useEffect(() => {
    log('mounted', { isApprover, isAuthed });
  }, [isApprover, isAuthed]);

  return null;
}
